import { resolveListingButtons } from '@pulsify/shared'
import {
  LISTING_BUTTON_KINDS,
  type ListingButtonAvailability,
  type ListingButtonKind,
  type ListingButtonsState,
  MAX_LISTING_BUTTONS,
  type UpdateListingButtonsRequest,
} from '@pulsify/shared/dto'

export interface ButtonsDraft {
  mode: 'auto' | 'own'
  /** The owner's kinds in order; kept while the mode is automatic so switching back restores them. */
  chosen: ListingButtonKind[]
}

/**
 * The card's starting point. Availability always comes from the server: there a Mittagsmenü
 * counts as soon as it is saved, so it can be chosen on a day guests do not see it.
 */
export function draftFromState(state: ListingButtonsState): ButtonsDraft {
  return state.chosen === null
    ? { mode: 'auto', chosen: resolveListingButtons(null, state.available) }
    : { mode: 'own', chosen: [...state.chosen] }
}

/** The row the phone preview shows for the draft. */
export function previewButtons(
  draft: ButtonsDraft,
  available: ListingButtonAvailability,
): ListingButtonKind[] {
  return resolveListingButtons(draft.mode === 'auto' ? null : draft.chosen, available)
}

interface ChooserRow {
  kind: ListingButtonKind
  /** Place in the owner's order, or null when the kind is not chosen. */
  position: number | null
  available: boolean
  canToggle: boolean
  canMoveUp: boolean
  canMoveDown: boolean
}

/** Every kind once: the chosen ones in the owner's order, then the available ones, then the rest. */
export function chooserRows(
  chosen: readonly ListingButtonKind[],
  available: ListingButtonAvailability,
): ChooserRow[] {
  const rest = LISTING_BUTTON_KINDS.filter((kind) => !chosen.includes(kind))
  const order = [
    ...chosen,
    ...rest.filter((kind) => available[kind]),
    ...rest.filter((kind) => !available[kind]),
  ]
  const full = chosen.length >= MAX_LISTING_BUTTONS
  return order.map((kind) => {
    const index = chosen.indexOf(kind)
    const position = index === -1 ? null : index
    return {
      kind,
      position,
      available: available[kind],
      canToggle: position === null ? available[kind] && !full : chosen.length > 1,
      canMoveUp: position !== null && position > 0,
      canMoveDown: position !== null && position < chosen.length - 1,
    }
  })
}

/** Ticks or unticks a kind; at most four, never fewer than one, nothing that is not available. */
export function toggleButton(
  chosen: readonly ListingButtonKind[],
  kind: ListingButtonKind,
  available: ListingButtonAvailability,
): ListingButtonKind[] {
  if (chosen.includes(kind)) {
    return chosen.length > 1 ? chosen.filter((entry) => entry !== kind) : [...chosen]
  }
  if (!available[kind] || chosen.length >= MAX_LISTING_BUTTONS) return [...chosen]
  return [...chosen, kind]
}

export function moveButton(
  chosen: readonly ListingButtonKind[],
  kind: ListingButtonKind,
  direction: -1 | 1,
): ListingButtonKind[] {
  const from = chosen.indexOf(kind)
  const to = from + direction
  if (from === -1 || to < 0 || to >= chosen.length) return [...chosen]
  const next = [...chosen]
  next.splice(from, 1)
  next.splice(to, 0, kind)
  return next
}

export function toButtonsRequest(draft: ButtonsDraft): UpdateListingButtonsRequest {
  return { chosen: draft.mode === 'auto' ? null : [...draft.chosen] }
}

export function isButtonsDraftDirty(draft: ButtonsDraft, state: ListingButtonsState): boolean {
  const next = toButtonsRequest(draft).chosen
  const saved = state.chosen
  if (next === null || saved === null) return next !== saved
  return next.length !== saved.length || next.some((kind, index) => kind !== saved[index])
}
